import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { EyeIcon, EyeSlashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    inviteCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState('idle'); // 'idle', 'checking', 'available', 'taken', 'invalid'
  
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.length < 3) {
      setUsernameStatus('idle');
      return;
    }

    // Basic format validation
    if (!/^[a-zA-Z0-9][a-zA-Z0-9_]*$/.test(username)) {
      setUsernameStatus('invalid');
      return;
    }

    setUsernameStatus('checking');
    try {
      const response = await authAPI.checkUsernameAvailability(username);
      if (response.data.available) {
        setUsernameStatus('available');
      } else {
        setUsernameStatus('taken');
      }
    } catch (error) {
      console.error('Username availability check failed:', error);
      setUsernameStatus('idle'); // Reset on error
    }
  }, []);

  // Debounce the username check
  useEffect(() => {
    if (formData.username) {
      const timeoutId = setTimeout(() => {
        checkUsernameAvailability(formData.username);
      }, 500); // 500ms delay

      return () => clearTimeout(timeoutId);
    } else {
      setUsernameStatus('idle');
    }
  }, [formData.username, checkUsernameAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Reset username status when username changes
    if (name === 'username') {
      setUsernameStatus('idle');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username must be no more than 30 characters';
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9_]*$/.test(formData.username)) {
      newErrors.username = 'Username must start with a letter or number and can only contain letters, numbers, and underscores';
    } else if (usernameStatus === 'taken') {
      newErrors.username = 'Username is already taken';
    } else if (usernameStatus === 'invalid') {
      newErrors.username = 'Invalid username format';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter, one uppercase letter, and one number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Invite code validation - check if required from environment or API
    // For now, we'll assume it's optional unless the backend rejects it
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);
    
    if (result.success) {
      toast.success('Account created successfully! Welcome to OKIO!');
      navigate('/');
    } else {
      // Handle server-side errors
      const errorMessage = result.message;
      
      // Check for specific field errors and set them appropriately
      if (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('taken')) {
        setErrors(prev => ({ ...prev, username: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('exists')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else {
        // For other errors, show toast notification
        toast.error(errorMessage);
      }
    }
  };

  return (
    <div className="mobile-auth-container relative overflow-hidden">
      {/* Mobile Background Image */}
      <div className="lg:hidden absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/covers/signup_cover_1.webp')`
        }}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-gray-900/60 to-black/80"></div>
      </div>

      {/* Back Button - Top Left (Mobile) */}
      <div className="lg:hidden absolute top-4 left-4 z-20">
        <Link
          to="/"
          className="flex items-center justify-center w-10 h-10 bg-black/20 hover:bg-black/30 rounded-full backdrop-blur-sm transition-colors"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
      </div>

      {/* Logo - Top Center (Mobile) */}
      <div className="lg:hidden absolute top-6 left-1/2 transform -translate-x-1/2 z-20">
        <div className="text-center">
          <img 
            src="/logo_black.webp" 
            alt="OKIO" 
            className="h-8 w-auto mx-auto"
          />
        </div>
      </div>

      {/* Welcome Text on Image (Mobile) */}
      {/* <div className="lg:hidden absolute top-[10%] left-4 z-20">
        <h1 className="text-4xl sm:text-4xl font-black text-white">Create Account</h1>
      </div> */}

      {/* Layout Container */}
      <div className="relative z-10 h-full flex flex-col lg:flex-row">
        {/* Desktop Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/covers/signup_cover_1.webp')`
            }}
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-gray-900/60 to-black/80"></div>
          </div>
        </div>

        {/* Form Section */}
        <div className="flex-1 lg:w-1/2 flex items-end lg:items-center justify-center lg:bg-white lg:dark:bg-gray-900 lg:py-12 px-0 lg:px-8">
        <div className="w-full lg:max-w-md bg-white dark:bg-gray-900 lg:bg-transparent rounded-t-3xl lg:rounded-none shadow-2xl lg:shadow-none px-6 pt-16 pb-5 lg:p-0 lg:pt-0 space-y-4 lg:space-y-8">
          <div className="text-center mb-6 lg:mb-6">
            {/* Show logo and text on desktop only - ensure completely hidden on mobile */}
            <img 
              src="/logo_black.webp" 
              alt="OKIO" 
              className="h-8 w-auto mx-auto mb-4 hidden lg:block dark:lg:hidden"
            />
            <img 
              src="/logo_white.webp" 
              alt="OKIO" 
              className="h-8 w-auto mx-auto mb-4 hidden dark:lg:block"
            />
            <h2 className="mt-0 lg:mt-4 text-xl font-extrabold text-gray-900 dark:text-white">
              Create your account
            </h2>
          </div>
          
          <form className="mt-0 lg:mt-0 space-y-4 lg:space-y-6" onSubmit={handleSubmit}>
            {/* OAuth Options - At the top */}
            <div>
              {/* Google Sign-In Button */}
              <div>
                <button
                  type="button"
                  onClick={async () => {
                    const { authAPI } = await import('../../services/api');
                    authAPI.googleOAuthWeb();
                  }}
                  className="w-full flex items-center justify-center gap-3 py-3 lg:py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign up with Google
                </button>
              </div>

              {/* OAuth Divider */}
              <div className="relative mt-4 mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">Or continue with email</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 lg:space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    First name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800"
                    placeholder="First name"
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800"
                    placeholder="Last name"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Username
                </label>
                <div className="mt-1 relative">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                      errors.username 
                        ? 'border-red-400' 
                        : usernameStatus === 'available' 
                          ? 'border-green-400' 
                          : usernameStatus === 'taken' || usernameStatus === 'invalid'
                            ? 'border-red-400'
                            : 'border-gray-300 dark:border-gray-600'
                    } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                    placeholder="Choose a username"
                  />
                  
                  {/* Status indicator */}
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {usernameStatus === 'checking' && formData.username.length >= 3 && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-300"></div>
                    )}
                    {usernameStatus === 'available' && (
                      <CheckIcon className="h-5 w-5 text-green-400" />
                    )}
                    {(usernameStatus === 'taken' || usernameStatus === 'invalid') && (
                      <XMarkIcon className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                </div>
                
                {/* Username feedback messages */}
                {errors.username && (
                  <p className="mt-1 text-sm text-red-400">{errors.username}</p>
                )}
                {!errors.username && usernameStatus === 'available' && (
                  <p className="mt-1 text-sm text-green-400">✓ Username is available</p>
                )}
                {!errors.username && usernameStatus === 'taken' && (
                  <p className="mt-1 text-sm text-red-400">✗ Username is already taken</p>
                )}
                {!errors.username && usernameStatus === 'invalid' && (
                  <p className="mt-1 text-sm text-red-400">✗ Username must start with a letter or number and can only contain letters, numbers, and underscores</p>
                )}
                {!errors.username && formData.username && formData.username.length >= 3 && usernameStatus === 'checking' && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Checking availability...</p>
                )}
                {!formData.username && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">3-30 characters, start with letter/number, use letters, numbers, and underscores</p>
                )}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.email ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Invite code
                </label>
                <input
                  id="inviteCode"
                  name="inviteCode"
                  type="text"
                  autoComplete="off"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.inviteCode ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                  } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                  placeholder="Enter your invite code (if required)"
                />
                {errors.inviteCode && (
                  <p className="mt-1 text-sm text-red-400">{errors.inviteCode}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                      errors.password ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                    } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                      errors.confirmPassword ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                    } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-gray-800`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 lg:py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 dark:focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create account'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Already have an account?</p>
              <Link
                to="/login"
                className="block w-full py-3 px-6 text-center text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
              >
                Sign in here
              </Link>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm; 